import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import apiService from '../../services/api';

const POSLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [heldCount, setHeldCount] = useState(0);

  // Initialize user synchronously to avoid flash
  const [user] = useState<any>(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (!token || !userData) return null;
      const parsed = JSON.parse(userData);
      const roles = parsed.roles?.map((r: any) => r.name) || [];
      const hasPosAccess = roles.some((r: string) =>
        ['super_admin', 'pos_admin', 'pos_cashier'].includes(r)
      );
      return hasPosAccess ? parsed : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch held sales count
  useEffect(() => {
    if (!user) return;
    const fetchHeldCount = async () => {
      try {
        const response = await apiService.get('/pos/sales/held');
        const sales = Array.isArray(response) ? response : response.data || [];
        setHeldCount(sales.length);
      } catch {}
    };
    fetchHeldCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchHeldCount, 30000);
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  if (!user) return (
    <div className="h-screen bg-gray-900" />
  );

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="h-screen flex flex-col bg-gray-900 text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top Bar */}
      <header className="h-14 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-4 shrink-0 z-10">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-white">SIGNATURE POS</h1>
          <nav className="flex gap-1 ml-6">
            <Link
              to="/pos"
              className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                isActive('/pos') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              Terminal
            </Link>
            <Link
              to="/pos/sales"
              className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                isActive('/pos/sales') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              Sales
            </Link>
            <Link
              to="/pos/held"
              className={`px-3 py-1.5 rounded text-sm font-medium transition relative ${
                isActive('/pos/held') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              Held
              {heldCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {heldCount}
                </span>
              )}
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">
            {user.name} ({user.roles?.[0]?.display_name || 'Staff'})
          </span>
          {user.roles?.some((r: any) => ['super_admin', 'pos_admin'].includes(r.name)) && (
            <Link to="/admin" className="text-xs text-gray-500 hover:text-gray-300">
              Admin Panel
            </Link>
          )}
          <button
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              window.dispatchEvent(new CustomEvent('authStateChanged'));
              navigate('/login');
            }}
            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-500 text-white rounded transition"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 min-h-0 overflow-hidden bg-gray-900">
        <Outlet />
      </main>
    </div>
  );
};

export default POSLayout;
